import { Injectable } from '@angular/core';
import { ReadyHttpClient } from './ready-http-client';
import { ICustomerFieldResponse, IInvoice } from '../models';
import { LocationStore } from '../stores/location.store';
import { TableStore } from '../stores/table.store';
import { OrderPayStore } from '../stores/order-pay.store';
import { OrderStore } from '../stores/order.store';
import { OrderMenuCartStore } from '../stores/order-menu-cart.store';
import { ItemModifierUI } from '../pages/v2-order-pay/models/item-modifier-ui.model';
import { LoyaltyStore } from '../stores/loyalty.store';
import { PromoCodeStore } from '../stores/promo-code.store';
import { OrderAheadStore } from '../stores/order-ahead.store';
import {
  IMenuV2Contract,
  IMenuV2ModifierGroup
} from './contracts/menu-v2.contracts';
import { RxUtil } from 'utility/rx.util';
import { OnAccountPaymentStore } from 'stores/on-account-payment.store';
import { ICustomerField } from '../models/customer-field.model';

interface IModifiersPayloadOptions {
  optionId: string;
  comment: string;
  modifiers: IModifiersPayloadOptions[];
}

@Injectable({
  providedIn: 'root'
})
export class OrderMenuApi {
  constructor(
    private httpClient: ReadyHttpClient,
    private locationStore: LocationStore,
    private tableStore: TableStore,
    private orderPayStore: OrderPayStore,
    private orderStore: OrderStore,
    private orderMenuCartStore: OrderMenuCartStore,
    private loyaltyStore: LoyaltyStore,
    private promoCodeStore: PromoCodeStore,
    private orderAheadStore: OrderAheadStore,
    private onAccountPaymentStore: OnAccountPaymentStore
  ) {}

  getMenuV2(): Promise<IMenuV2Contract> {
    return this.httpClient.get<IMenuV2Contract>(
      `/v2/locations/${this.locationStore.currentLocation?._id}/menus/guestmenus`
    );
  }

  getMenuV2ModifierGroups(itemId: string): Promise<IMenuV2ModifierGroup[]> {
    return this.httpClient.get<IMenuV2ModifierGroup[]>(
      `/v2/locations/${this.locationStore.currentLocation?._id}/menus/guestmenus/items/${itemId}/modifiergroups`
    );
  }

  private async getOnAccountFieldResponses(): Promise<
    ICustomerFieldResponse[]
  > {
    const fields = await RxUtil.takeOne(
      this.onAccountPaymentStore.selectedOnAccountPaymentMethodCustomerFields$
    );
    return fields
      .filter(cf => cf.inputType !== 'hidden')
      .map((cf) => ({
        id: cf.inputLabel,
        value: cf.customerResponse
      }));
  }

  async buildSubmitOrderPayload(): Promise<object> {
    const promoCode = await RxUtil.takeOne(
      this.promoCodeStore.currentAppliedPromoCode$
    );

    const isUserPromoCodeApplied = await RxUtil.takeOne(
      this.promoCodeStore.isUserPromoCodeApplied$
    );

    // do not send loyalty to API for delayed payment orders
    let selectedLoyaltyReward: string | undefined;
    if (
      !this.locationStore.currentLocation
        ?.orderDoesNotRequireImmediatePayment &&
      this.loyaltyStore.appliedOffer &&
      !this.loyaltyStore.appliedOffer.isInvalid
    ) {
      selectedLoyaltyReward = this.loyaltyStore.appliedOffer.id;
    }

    const selectedOnAccountPaymentMethod = await RxUtil.takeOne(
      this.onAccountPaymentStore.selectedOnAccountPaymentMethod$
    );

    const customerFields = await RxUtil.takeOne(
      this.orderStore.customerFields$
    );

    return {
      locationId: this.locationStore.currentLocation?._id,
      order: {
        name: '',
        fulfillmentDetails: {
          tableId: this.tableStore.currentTable?.externalId,
          tableName: this.tableStore.currentTable?.name,
          targetCode: this.orderPayStore.beaconId,
          orderAheadTime: await RxUtil.takeOne(
            this.orderAheadStore.selectedDateStringToSendToAPI$
          ),
          customerFieldResponses: this.orderStore.customerFieldResponses.filter(
            response => {
              const field = customerFields.find(
                f => f.inputLabel === response.id
              );
              return field && field.inputType !== 'hidden';
            }
          ),
          accountPaymentOption: selectedOnAccountPaymentMethod
            ? {
                _id: selectedOnAccountPaymentMethod.id,
                fieldResponses: await this.getOnAccountFieldResponses()
              }
            : undefined
        },
        items: this.orderMenuCartStore.cartEntries.map((cartEntry) => ({
          itemId: cartEntry.item.id,
          quantity: cartEntry.quantity,
          name: cartEntry.item.name,
          price: cartEntry.item.price,
          modifiers: cartEntry.modifierGroups.map((mg) => ({
            groupId: mg.id,
            modifiers: this.mapModifiersForPayload(mg.modifiers)
          })),
          comment: cartEntry.comment
        })),
        promoCode: promoCode && promoCode.code,
        promoAutoAttached:
          promoCode && promoCode.code ? !isUserPromoCodeApplied : null,
        selectedReward: selectedLoyaltyReward
      }
    };
  }

  // TODO move to orders.api, also move service method that uses this
  async submitOrder(): Promise<IInvoice> {
    const response = await this.httpClient.post<{ invoice: IInvoice }>(
      '/orders/submit',
      await this.buildSubmitOrderPayload()
    );
    return response.invoice;
  }

  private mapModifiersForPayload(
    modifiers: ItemModifierUI[]
  ): IModifiersPayloadOptions[] {
    return modifiers.map((modifier) => ({
      optionId: modifier.id,
      comment: modifier.commentText,
      name: modifier.name,
      price: modifier.price,
      quantity: modifier.quantity,
      modifiers: this.mapModifiersForPayload(
        modifier.modifierGroups.map((mg) => mg.modifiers).flat()
      )
    }));
  }
}
